import { toMetricNumber } from './normalize';
import { getTargetFromMetricData } from './metric-target';
import {
  BuildV2FromDataSourceInput,
  BuildV2FromKpiDataInput,
  V2WizardConnectPayload,
} from './types';

function applyTargetFromTotal(
  payload: V2WizardConnectPayload,
  total: number | undefined,
): V2WizardConnectPayload {
  if (total === undefined) {
    return payload;
  }
  return {
    ...payload,
    target: total,
    ext_source_unit: {
      ...payload.ext_source_unit,
      target: total,
    },
  };
}

export function buildV2WizardPayloadFromDataSource(
  input: BuildV2FromDataSourceInput,
): V2WizardConnectPayload {
  const {
    dataSource,
    externalAppId,
    kpiDataId,
    userConnection,
    clientId,
  } = input;
  const projectObj = {
    name: dataSource.name || dataSource.id,
    id: dataSource.id,
    type: dataSource.type,
  };
  const metricData = dataSource.data;
  const targetFromTotal = getTargetFromMetricData(metricData);

  const payload: V2WizardConnectPayload = {
    clearCache: true,
    ext_app_id: externalAppId,
    source: 8,
    achieve_target: metricData.value,
    real_owner: input.realOwnerId,
    name: input.metricName,
    description: input.metricDescription,
    parent: input.parent,
    instance_url: userConnection.externalInstanceUrl,
    project_ids: [JSON.stringify(projectObj)],
    ext_source_data: {
      ...dataSource.extraData,
      kpiDataId,
      value: metricData.value,
      valueLabel: metricData.label,
      automateTargetFlag: dataSource.automateTargetFlag,
    },
    external_app_sources: {
      id: kpiDataId,
      ...dataSource.extraData,
      value: metricData.value,
      valueLabel: metricData.label,
      automateTargetFlag: dataSource.automateTargetFlag,
      external_app_id: externalAppId,
      instance_url: userConnection.externalInstanceUrl,
      ext_app_link_name: dataSource.extraData?.workbookName as string | undefined,
      ext_app_link_type: projectObj.type,
      ext_app_link_id: projectObj.id,
    },
    ext_source_unit: {
      progress: metricData.value,
    },
    ext_source_string: clientId,
    connectionUrn: userConnection.connectionUrn,
  };

  return applyTargetFromTotal(payload, targetFromTotal);
}

export function buildV2WizardPayloadFromKpiData(
  input: BuildV2FromKpiDataInput,
): V2WizardConnectPayload {
  const { kpiData, externalAppId, userConnection, clientId } = input;
  const projectObj = {
    name: kpiData.externalObjectName || kpiData._id,
    id: kpiData._id,
    type: 'kpi',
  };
  const metricData = kpiData.data;
  const targetFromTotal = getTargetFromMetricData(metricData);

  const payload: V2WizardConnectPayload = {
    clearCache: true,
    ext_app_id: externalAppId,
    source: 8,
    achieve_target: metricData.value,
    real_owner: input.realOwnerId,
    name: input.metricName,
    description: input.metricDescription,
    parent: input.parent,
    instance_url: userConnection.externalInstanceUrl,
    project_ids: [JSON.stringify(projectObj)],
    ext_source_data: {
      kpiDataId: kpiData._id,
      connectorId: kpiData.externalObjectExtra?.connectorId != null
        ? String(kpiData.externalObjectExtra.connectorId)
        : undefined,
      webUrl: kpiData.externalObjectExtra?.webUrl != null
        ? String(kpiData.externalObjectExtra.webUrl)
        : undefined,
    },
    external_app_sources: {
      id: kpiData._id,
      external_app_id: externalAppId,
      instance_url: userConnection.externalInstanceUrl,
      automateTargetFlag: kpiData.automateTargetFlag,
      ext_app_link_name: projectObj.name,
      ext_app_link_type: projectObj.type,
      ext_app_link_id: projectObj.id,
    },
    ext_source_unit: {
      progress: metricData.value,
    },
    ext_source_string: clientId,
    connectionUrn: userConnection.connectionUrn,
  };

  return applyTargetFromTotal(payload, targetFromTotal);
}

/** Coerce real_owner / parent from wizard props at the popup boundary. */
export function toWizardOwnerId(value: unknown): number | undefined {
  return toMetricNumber(value);
}
